/* eslint-disable no-underscore-dangle */
import React, { useState, useContext } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Box,
  Stack,
  Typography,
  MenuItem,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useMutation } from '@apollo/client'
import { useNavigate } from 'react-router-dom'

import createAdminMutation from '../../graphql/mutation/createAdmin'
import createStaffMutation from '../../graphql/mutation/createStaff'
import createOrganizerMutation from '../../graphql/mutation/createOrganizer'
import setPasswordMutation from '../../graphql/mutation/setPassword'
import AuthContext from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'

const CreateUserDialog = ({ open, onClose }) => {
  const { showSuccess, showError } = useToast()
  const [userName, setUserName] = useState('')
  const [loginName, setLoginName] = useState('')
  const [pass1, setPass1] = useState('')
  const [pass2, setPass2] = useState('')
  const [role, setRole] = useState('Organizer')
  const [error, setError] = useState('')

  const [createAdmin] = useMutation(createAdminMutation)
  const [createStaff] = useMutation(createStaffMutation)
  const [createOrganizer] = useMutation(createOrganizerMutation)
  const [setPassword] = useMutation(setPasswordMutation)

  const navigate = useNavigate()
  const { user } = useContext(AuthContext)

  const roles = ['Organizer', 'Staff']
  if (user.role === 'Admin') {
    roles.push('Admin')
  }

  const handleClose = () => {
    setUserName('')
    setLoginName('')
    setPass1('')
    setPass2('')
    setRole('Organizer')
    setError('')
    onClose()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!userName || !loginName || !pass1 || !pass2) {
      setError('Please fill in all fields')
      return
    }

    if (pass1 !== pass2) {
      setError('Passwords do not match')
      return
    }

    try {
      if (role === 'Admin') {
        const data = await createAdmin({
          variables: {
            record: {
              createdById: user._id,
              name: userName,
              username: loginName,
            },
          },
        })
        await setPassword({
          variables: {
            id: data.data.createAdminUser.recordId,
            password: pass1,
          },
        })
        showSuccess('Admin user created successfully!', 'Success')
        handleClose()
        navigate('/users')
      } else if (role === 'Organizer') {
        const data = await createOrganizer({
          variables: {
            record: {
              createdById: user._id,
              name: userName,
              username: loginName,
            },
          },
        })
        await setPassword({
          variables: {
            id: data.data.createOrganizerUser.recordId,
            password: pass1,
          },
        })
        showSuccess('Organizer user created successfully!', 'Success')
        handleClose()
        navigate('/users')
      } else {
        const data = await createStaff({
          variables: {
            record: {
              createdById: user._id,
              organizerId: user._id,
              name: userName,
              username: loginName,
            },
          },
        })
        await setPassword({
          variables: {
            id: data.data.createStaffUser.recordId,
            password: pass1,
          },
        })
        showSuccess('Staff user created successfully!', 'Success')
        handleClose()
        navigate('/users')
      }
    } catch (err) {
      console.error('Error creating user:', err)
      const errorMessage =
        err.message || err.graphQLErrors?.[0]?.message || 'Failed to create user. Please try again.'
      setError(errorMessage)
      showError(errorMessage, 'Create User Failed')
    }
  }

  if (user.role === 'Staff') {
    return null
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
          backgroundColor: 'background.paper',
        },
      }}>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: { xs: 1.5, sm: 2, md: 2.5 },
          borderBottom: (theme) =>
            `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
        }}>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: 600,
            fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
            color: 'text.primary',
          }}>
          Add New User
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? 'rgba(0, 0, 0, 0.04)'
                  : 'rgba(255, 255, 255, 0.08)',
            },
          }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: { xs: 2, sm: 2.5, md: 3 } }}>
          <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {/* Row 1: Name and Username */}
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  placeholder="Name"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  required
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                      borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.02)'
                          : 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'rgba(255, 255, 255, 0.08)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'rgba(255, 255, 255, 0.08)',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                      fontWeight: 500,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Username"
                  placeholder="Username"
                  value={loginName}
                  onChange={(e) => setLoginName(e.target.value)}
                  required
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                      borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.02)'
                          : 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'rgba(255, 255, 255, 0.08)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'rgba(255, 255, 255, 0.08)',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                      fontWeight: 500,
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Row 2: Password and Confirm Password */}
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Password"
                  placeholder="Password"
                  type="password"
                  value={pass1}
                  onChange={(e) => setPass1(e.target.value)}
                  required
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                      borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.02)'
                          : 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'rgba(255, 255, 255, 0.08)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'rgba(255, 255, 255, 0.08)',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                      fontWeight: 500,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Confirm Password"
                  placeholder="Confirm Password"
                  type="password"
                  value={pass2}
                  onChange={(e) => setPass2(e.target.value)}
                  required
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                      borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.02)'
                          : 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'rgba(255, 255, 255, 0.08)',
                      },
                      '&.Mui-focused': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'rgba(255, 255, 255, 0.08)',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                      fontWeight: 500,
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Row 3: Role */}
            <TextField
              select
              label="Role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              required
              fullWidth
              size="small"
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
                      mt: 0.5,
                    },
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.02)'
                      : 'rgba(255, 255, 255, 0.05)',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.04)'
                        : 'rgba(255, 255, 255, 0.08)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.04)'
                        : 'rgba(255, 255, 255, 0.08)',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                  fontWeight: 500,
                },
              }}>
              {roles.map((roleOption) => (
                <MenuItem key={roleOption} value={roleOption}>
                  {roleOption}
                </MenuItem>
              ))}
            </TextField>

            {error && (
              <Box
                sx={{
                  p: { xs: 1, sm: 1.25, md: 1.5 },
                  borderRadius: { xs: 0.75, sm: 1, md: 1.25 },
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(244, 67, 54, 0.1)'
                      : 'rgba(244, 67, 54, 0.2)',
                  color: 'error.main',
                  fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
                }}>
                {error}
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 2.5, md: 3 },
            py: { xs: 1.5, sm: 2, md: 2.5 },
            borderTop: (theme) =>
              `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
            gap: { xs: 1, sm: 1.5, md: 2 },
          }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            color="primary"
            sx={{
              borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 0.75, sm: 0.875, md: 1 },
              fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
              fontWeight: 500,
              textTransform: 'none',
            }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{
              borderRadius: { xs: 1, sm: 1.25, md: 1.5 },
              px: { xs: 2, sm: 2.5, md: 3 },
              py: { xs: 0.75, sm: 0.875, md: 1 },
              fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
              fontWeight: 600,
              textTransform: 'none',
            }}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default CreateUserDialog
